/**
 * DataView Component
 *
 * Professional data analysis visualization with:
 * - ERD (Entity Relationship Diagram) - D3 force-directed graph
 * - Data Lineage - Sankey-style flow visualization
 * - Data Structures - Hierarchical field browser
 * - Copybook Analysis - Usage and dependency analysis
 */

import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import * as d3 from 'd3'
import {
  ArrowLeft, Database, GitBranch, Table2, FileStack,
  Loader2, AlertCircle, RefreshCw, ZoomIn, ZoomOut,
  ChevronRight, ChevronDown, Search, X, Download, Info
} from 'lucide-react'
import {
  getDataAnalysisData,
  getCrossReference,
  getJsonArtifact,
  getJobResults,
} from '../../services/codeAnalysisService'
import './DataView.css'

/**
 * Tab configuration
 */
const TABS = {
  'erd': {
    label: 'Entity Diagram',
    icon: Database,
    description: 'Entity-Relationship visualization',
  },
  'lineage': {
    label: 'Data Lineage',
    icon: GitBranch,
    description: 'Data flow through programs',
  },
  'structures': {
    label: 'Data Structures',
    icon: Table2,
    description: 'COBOL data definitions',
  },
  'copybooks': {
    label: 'Copybooks',
    icon: FileStack,
    description: 'Copybook dependencies',
  },
}

/**
 * Color palette
 */
const COLORS = {
  entity: '#7aa2f7',
  copybook: '#9ece6a',
  file: '#e0af68',
  table: '#bb9af7',
  field: '#7dcfff',
  relationship: '#565f89',
  highlight: '#ff9e64',
}

/**
 * Code Analysis fallback transforms
 *
 * These convert Code Analysis artifacts into the shapes
 * expected by the four DataView tab components, used when
 * dedicated Data Analysis artifacts are absent or empty.
 */

/**
 * crossRef.copybook_usage shape: { "COMMWS": ["CUSTUPD.cbl", "CUSTVAL.cbl"], ... }
 * Keys = copybook names, Values = programs that COPY them.
 *
 * Build CopybookAnalysis input — enrich fields from unified_data_model items.
 */
function buildCopybooksFromCrossRef(crossRef, unifiedItems) {
  const copybookUsage = crossRef?.copybook_usage || {}
  if (Object.keys(copybookUsage).length === 0) return null

  const list = Object.entries(copybookUsage).map(([cbName, usedBy]) => {
    // Match field items for this copybook (source_file = "COMMWS.cpy" etc.)
    const cbBase = cbName.toLowerCase().replace(/\.(cpy|CPY)$/, '')
    const fields = (unifiedItems || [])
      .filter(item => item.source_type === 'copybook' &&
        (item.source_file || '').toLowerCase().replace(/\.(cpy|CPY)$/, '') === cbBase)
      .map(f => ({ name: f.name, type: f.pic || (f.is_group ? 'GROUP' : 'FIELD') }))
    return {
      name: cbName,
      used_by: Array.isArray(usedBy) ? usedBy : [],
      fields,
    }
  })
  return { copybooks: list }
}

/**
 * Build DataStructuresBrowser from unified_data_model.items grouped by source_file.
 * Shows copybook structures first (the shared contracts), then program-local items.
 */
function buildStructuresFromUnifiedModel(unifiedItems) {
  if (!unifiedItems?.length) return null

  const bySource = {}
  unifiedItems.forEach(item => {
    const src = item.source_file || 'Unknown'
    if (!bySource[src]) bySource[src] = []
    bySource[src].push({
      level: item.level || 1,
      name: item.name || 'UNKNOWN',
      type: item.pic || (item.is_group ? 'GROUP' : null),
      occurs: item.occurs || null,
    })
  })

  // Copybooks first, then programs
  const sorted = Object.entries(bySource).sort(([a], [b]) => {
    const aCopy = a.endsWith('.cpy') || a.endsWith('.CPY') ? 0 : 1
    const bCopy = b.endsWith('.cpy') || b.endsWith('.CPY') ? 0 : 1
    return aCopy - bCopy || a.localeCompare(b)
  })

  const programs = sorted.map(([src, items]) => ({
    source_file: src,
    data_items: items,
  }))

  return { programs, summary: { total_data_items: unifiedItems.length } }
}

/**
 * Build ERD from crossRef.copybook_usage — bipartite: programs ↔ copybooks.
 * Keys = copybook names, Values = programs using them.
 * Enrich copybook nodes with real field counts from unified_data_model.
 */
function buildERDFromCrossRef(crossRef, unifiedItems) {
  const copybookUsage = crossRef?.copybook_usage || {}
  const copybooks = Object.keys(copybookUsage)
  if (copybooks.length === 0) return null

  // Collect unique programs
  const programSet = new Set()
  Object.values(copybookUsage).forEach(progs => {
    if (Array.isArray(progs)) progs.forEach(p => programSet.add(p))
  })

  // Count fields per copybook source_file
  const fieldsByCopybook = {}
  ;(unifiedItems || []).forEach(item => {
    if (item.source_type === 'copybook') {
      const key = (item.source_file || '').toLowerCase().replace(/\.(cpy|CPY)$/, '')
      fieldsByCopybook[key] = (fieldsByCopybook[key] || 0) + 1
    }
  })

  const entities = [
    ...Array.from(programSet).map(prog => ({
      name: prog.replace(/\.(cbl|CBL)$/, ''),
      type: 'program',
      fields: [],
      fieldCount: 0,
      description: 'COBOL Program',
    })),
    ...copybooks.map(cb => {
      const cbBase = cb.toLowerCase().replace(/\.(cpy|CPY)$/, '')
      const fieldCount = fieldsByCopybook[cbBase] || 0
      return {
        name: cb.replace(/\.(cpy|CPY)$/, ''),
        type: 'copybook',
        fields: [],
        fieldCount,
        usedBy: (copybookUsage[cb] || []).length,
        description: `Shared data contract · ${fieldCount} fields`,
      }
    }),
  ]

  const relationships = []
  Object.entries(copybookUsage).forEach(([cb, progs]) => {
    const cbName = cb.replace(/\.(cpy|CPY)$/, '')
    ;(Array.isArray(progs) ? progs : []).forEach(prog => {
      relationships.push({
        from: prog.replace(/\.(cbl|CBL)$/, ''),
        to: cbName,
        type: 'USES',
      })
    })
  })

  return { entities, relationships }
}

/**
 * Build Data Lineage from call_graph + file access patterns in crossRef.
 */
function buildLineageFromCallGraph(crossRef, fileModel) {
  // Program-to-program CALL flows from cross_reference.call_graph
  const callGraph = crossRef?.call_graph || {}
  const flows = []

  Object.entries(callGraph).forEach(([caller, callees]) => {
    const callerName = caller.replace(/\.(cbl|CBL)$/, '')
    if (Array.isArray(callees)) {
      callees.forEach(callee => {
        const calleeName = (typeof callee === 'string' ? callee : callee.target || '')
          .replace(/\.(cbl|CBL)$/, '')
        if (calleeName) flows.push({ from: callerName, to: calleeName, type: 'CALLS' })
      })
    }
  })

  // File I/O flows — file_operations is an object {open:…, read:…, write:…}, not an array
  const selects = fileModel?.select_statements || []
  if (Array.isArray(selects) && selects.length > 0) {
    const fileOps = fileModel?.file_operations || {}
    const hasRead = 'read' in fileOps
    const hasWrite = 'write' in fileOps
    selects.forEach(sel => {
      const fname = sel.file_name
      if (!fname) return
      // Read files flow INTO the program; write files flow OUT
      if (hasRead) flows.push({ from: fname, to: 'CUSTUPD', type: 'FILE_READ' })
      if (hasWrite) flows.push({ from: 'CUSTUPD', to: fname, type: 'FILE_WRITE' })
    })
  }

  if (flows.length === 0) return null
  return { flows }
}

/**
 * Shared Data Contract Diagram — bipartite layout.
 * Programs (left) ↔ Copybooks / shared data structures (right).
 * Copybooks shared by 2+ programs are highlighted as hotspots.
 */
function ERDGraph({ erd }) {
  const svgRef = useRef(null)
  const containerRef = useRef(null)
  const [selected, setSelected] = useState(null)

  useEffect(() => {
    if (!erd?.entities?.length || !svgRef.current || !containerRef.current) return

    const container = containerRef.current
    const W = container.clientWidth || 800
    const H = container.clientHeight || 480

    const programs = erd.entities.filter(e => e.type === 'program')
    const copybooks = erd.entities.filter(e => e.type === 'copybook')
    const relationships = erd.relationships || []

    if (programs.length === 0 && copybooks.length === 0) return

    const PAD = 60
    const progX = 150
    const cbX = W - 200
    const progRowH = Math.min(80, (H - PAD * 2) / Math.max(programs.length, 1))
    const cbRowH = Math.min(80, (H - PAD * 2) / Math.max(copybooks.length, 1))

    // Fixed Y positions
    programs.forEach((p, i) => {
      p._y = PAD + i * progRowH + progRowH / 2
    })
    copybooks.forEach((c, i) => {
      c._y = PAD + i * cbRowH + cbRowH / 2
    })

    const progMap = new Map(programs.map(p => [p.name, p]))
    const cbMap = new Map(copybooks.map(c => [c.name, c]))

    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()
    svg.attr('width', W).attr('height', H)

    // Gradient defs for shared copybooks
    const defs = svg.append('defs')
    defs.append('marker')
      .attr('id', 'arrow-erd')
      .attr('viewBox', '0 -4 8 8')
      .attr('refX', 8).attr('refY', 0)
      .attr('markerWidth', 5).attr('markerHeight', 5)
      .attr('orient', 'auto')
      .append('path').attr('d', 'M0,-4L8,0L0,4').attr('fill', '#4a5568')

    // Draw edges (bezier curves)
    const edgeG = svg.append('g').attr('class', 'edges')
    relationships.forEach(rel => {
      const prog = progMap.get(rel.from)
      const cb = cbMap.get(rel.to)
      if (!prog || !cb) return

      const x1 = progX + 70, y1 = prog._y
      const x2 = cbX - 70, y2 = cb._y
      const mx = (x1 + x2) / 2

      const isShared = cb.usedBy > 1
      const edgePath = `M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}`

      edgeG.append('path')
        .attr('d', edgePath)
        .attr('fill', 'none')
        .attr('stroke', isShared ? '#e0af68' : '#4a5568')
        .attr('stroke-width', isShared ? 1.8 : 1.2)
        .attr('stroke-opacity', 0.55)
        .attr('marker-end', 'url(#arrow-erd)')
    })

    // Section labels
    svg.append('text').attr('x', progX).attr('y', PAD - 24)
      .attr('text-anchor', 'middle').attr('font-size', 11).attr('font-weight', 700)
      .attr('fill', '#7aa2f7').attr('letter-spacing', 1).text('PROGRAMS')

    svg.append('text').attr('x', cbX).attr('y', PAD - 24)
      .attr('text-anchor', 'middle').attr('font-size', 11).attr('font-weight', 700)
      .attr('fill', '#9ece6a').attr('letter-spacing', 1).text('DATA CONTRACTS')

    const NODE_W = 130, NODE_H = 44

    // Program nodes
    const progG = svg.append('g')
    programs.forEach(prog => {
      const g = progG.append('g')
        .attr('transform', `translate(${progX},${prog._y})`)
        .style('cursor', 'pointer')
        .on('click', () => setSelected(s => s?.name === prog.name ? null : prog))

      g.append('rect')
        .attr('x', -NODE_W / 2).attr('y', -NODE_H / 2)
        .attr('width', NODE_W).attr('height', NODE_H)
        .attr('rx', 6)
        .attr('fill', '#1a2744')
        .attr('stroke', '#7aa2f7').attr('stroke-width', 1.5)

      g.append('text').attr('text-anchor', 'middle').attr('dy', -4)
        .attr('fill', '#7aa2f7').attr('font-size', 12).attr('font-weight', 700)
        .text(prog.name)

      g.append('text').attr('text-anchor', 'middle').attr('dy', 12)
        .attr('fill', 'rgba(122,162,247,0.6)').attr('font-size', 9)
        .text('COBOL Program')
    })

    // Copybook nodes
    const cbG = svg.append('g')
    copybooks.forEach(cb => {
      const isHot = cb.usedBy >= 3
      const isShared = cb.usedBy >= 2
      const fill = isHot ? '#2d2215' : isShared ? '#202b1e' : '#1d2335'
      const stroke = isHot ? '#e0af68' : isShared ? '#9ece6a' : '#565f89'

      const g = cbG.append('g')
        .attr('transform', `translate(${cbX},${cb._y})`)
        .style('cursor', 'pointer')
        .on('click', () => setSelected(s => s?.name === cb.name ? null : cb))

      g.append('rect')
        .attr('x', -NODE_W / 2).attr('y', -NODE_H / 2)
        .attr('width', NODE_W).attr('height', NODE_H)
        .attr('rx', 6)
        .attr('fill', fill)
        .attr('stroke', stroke).attr('stroke-width', 1.5)

      g.append('text').attr('text-anchor', 'middle').attr('dy', -4)
        .attr('fill', isHot ? '#e0af68' : '#9ece6a').attr('font-size', 12).attr('font-weight', 700)
        .text(cb.name)

      g.append('text').attr('text-anchor', 'middle').attr('dy', 12)
        .attr('fill', 'rgba(158,206,106,0.55)').attr('font-size', 9)
        .text(`${cb.fieldCount} fields · ${cb.usedBy} program${cb.usedBy !== 1 ? 's' : ''}`)
    })

    // Legend
    const legG = svg.append('g').attr('transform', `translate(${W / 2 - 100}, ${H - 22})`)
    const items = [
      { color: '#e0af68', label: 'Shared by 3+ programs' },
      { color: '#9ece6a', label: 'Shared by 2 programs' },
      { color: '#565f89', label: 'Single program' },
    ]
    items.forEach((item, i) => {
      legG.append('rect').attr('x', i * 170).attr('y', 0).attr('width', 10).attr('height', 10)
        .attr('rx', 2).attr('fill', item.color)
      legG.append('text').attr('x', i * 170 + 14).attr('y', 9)
        .attr('fill', '#8b949e').attr('font-size', 9).text(item.label)
    })

  }, [erd])

  if (!erd?.entities?.length) {
    return (
      <div className="empty-state">
        <p>No entity data available for this run.</p>
      </div>
    )
  }

  const programs = erd.entities.filter(e => e.type === 'program')
  const copybooks = erd.entities.filter(e => e.type === 'copybook')

  return (
    <div className="erd-container" style={{ position: 'relative' }} ref={containerRef}>
      <svg ref={svgRef} width="100%" height="100%" />
      {selected && (
        <div className="entity-tooltip" style={{
          position: 'absolute', top: 12, right: 12, background: '#1a1b26',
          border: `1px solid ${selected.type === 'copybook' ? '#9ece6a' : '#7aa2f7'}`,
          borderRadius: 8, padding: '12px 16px', minWidth: 200, zIndex: 10,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h4 style={{ margin: 0, color: selected.type === 'copybook' ? '#9ece6a' : '#7aa2f7', fontSize: 14 }}>
              {selected.name}
            </h4>
            <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', color: '#8b949e', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>
          </div>
          <p style={{ margin: '6px 0 0', fontSize: 11, color: '#8b949e' }}>{selected.description}</p>
          {selected.type === 'copybook' && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 10, color: '#8b949e', marginBottom: 4 }}>USED BY</div>
              {erd.relationships.filter(r => r.to === selected.name).map(r => (
                <span key={r.from} style={{ display: 'inline-block', background: '#1a2744', border: '1px solid #7aa2f7', borderRadius: 4, padding: '2px 8px', fontSize: 10, color: '#7aa2f7', marginRight: 6, marginBottom: 4 }}>{r.from}</span>
              ))}
            </div>
          )}
          {selected.type === 'program' && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 10, color: '#8b949e', marginBottom: 4 }}>COPIES</div>
              {erd.relationships.filter(r => r.from === selected.name).map(r => (
                <span key={r.to} style={{ display: 'inline-block', background: '#202b1e', border: '1px solid #9ece6a', borderRadius: 4, padding: '2px 8px', fontSize: 10, color: '#9ece6a', marginRight: 6, marginBottom: 4 }}>{r.to}</span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Data Lineage Component - Sankey-style flow
 */
function DataLineageChart({ lineage }) {
  const svgRef = useRef(null)
  const containerRef = useRef(null)
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })

  // Track container dimensions
  useEffect(() => {
    if (!containerRef.current) return

    const updateDimensions = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect()
        if (width > 0 && height > 0) {
          setDimensions({ width, height })
        }
      }
    }

    const resizeObserver = new ResizeObserver(updateDimensions)
    resizeObserver.observe(containerRef.current)
    setTimeout(updateDimensions, 50)

    return () => resizeObserver.disconnect()
  }, [])

  useEffect(() => {
    if (!lineage?.flows?.length || !svgRef.current) return
    if (dimensions.width === 0 || dimensions.height === 0) return

    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()

    const { width, height } = dimensions

    const flows = lineage.flows || []

    // Build nodes and links from flows
    const nodeSet = new Set()
    flows.forEach(flow => {
      const source = flow.source || flow.from
      const target = flow.target || flow.to
      if (source) nodeSet.add(source)
      if (target) nodeSet.add(target)
    })

    const nodes = Array.from(nodeSet)
      .filter(name => name) // Filter out any undefined/null values
      .map((name, i) => ({
        id: name,
        name: String(name), // Ensure name is always a string
        x: (i % 3) * 250 + 100,
        y: Math.floor(i / 3) * 80 + 50,
      }))

    const nodeMap = new Map(nodes.map(n => [n.id, n]))

    // Create links
    const links = flows.map(f => ({
      source: nodeMap.get(f.source || f.from),
      target: nodeMap.get(f.target || f.to),
      type: f.type || 'data',
      field: f.field,
    })).filter(l => l.source && l.target)

    // Apply simple hierarchical layout
    const levels = {}
    nodes.forEach(n => { levels[n.id] = 0 })

    links.forEach(l => {
      if (l.target && levels[l.target.id] <= levels[l.source.id]) {
        levels[l.target.id] = levels[l.source.id] + 1
      }
    })

    const maxLevel = Math.max(...Object.values(levels))
    const levelWidth = width / (maxLevel + 2)

    const levelNodes = {}
    nodes.forEach(n => {
      const level = levels[n.id]
      if (!levelNodes[level]) levelNodes[level] = []
      levelNodes[level].push(n)
    })

    Object.entries(levelNodes).forEach(([level, nodeList]) => {
      const levelHeight = height / (nodeList.length + 1)
      nodeList.forEach((n, i) => {
        n.x = parseInt(level) * levelWidth + levelWidth / 2
        n.y = (i + 1) * levelHeight
      })
    })

    // Draw links
    const linkPath = d3.linkHorizontal()
      .x(d => d.x)
      .y(d => d.y)

    svg.append('g')
      .attr('class', 'links')
      .selectAll('path')
      .data(links)
      .join('path')
      .attr('d', d => linkPath({ source: d.source, target: d.target }))
      .attr('fill', 'none')
      .attr('stroke', COLORS.relationship)
      .attr('stroke-width', 2)
      .attr('stroke-opacity', 0.6)

    // Draw nodes
    const node = svg.append('g')
      .attr('class', 'nodes')
      .selectAll('g')
      .data(nodes)
      .join('g')
      .attr('transform', d => `translate(${d.x},${d.y})`)

    node.append('rect')
      .attr('width', 100)
      .attr('height', 36)
      .attr('x', -50)
      .attr('y', -18)
      .attr('rx', 4)
      .attr('fill', COLORS.entity)

    node.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', 4)
      .attr('fill', '#fff')
      .attr('font-size', 10)
      .text(d => {
        const name = d.name || 'Unknown'
        return name.length > 12 ? name.slice(0, 10) + '...' : name
      })

  }, [lineage, dimensions])

  if (!lineage?.flows?.length) {
    return (
      <div className="empty-state">
        <p>No file I/O flows detected. Data lineage requires READ/WRITE statements in the COBOL PROCEDURE DIVISION.</p>
      </div>
    )
  }

  return (
    <div className="lineage-container" ref={containerRef}>
      <svg ref={svgRef} width="100%" height="100%" />
    </div>
  )
}

/**
 * Data Structures Browser
 */
function DataStructuresBrowser({ structures }) {
  const [expandedItems, setExpandedItems] = useState(new Set())
  const [searchTerm, setSearchTerm] = useState('')

  const toggleExpand = useCallback((id) => {
    setExpandedItems(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  const filteredPrograms = useMemo(() => {
    if (!structures?.programs) return []

    if (!searchTerm) return structures.programs

    return structures.programs.map(prog => ({
      ...prog,
      data_items: prog.data_items?.filter(item =>
        item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.type?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    })).filter(prog => prog.data_items?.length > 0)
  }, [structures, searchTerm])

  if (!structures?.programs?.length) {
    return (
      <div className="empty-state">
        <p>No data structure information available for this run.</p>
      </div>
    )
  }

  return (
    <div className="structures-browser">
      <div className="structures-toolbar">
        <div className="search-box">
          <Search size={14} />
          <input
            type="text"
            placeholder="Search fields..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')}>
              <X size={12} />
            </button>
          )}
        </div>
        <div className="structures-stats">
          {structures.summary?.total_data_items || 0} fields
        </div>
      </div>

      <div className="structures-list">
        {filteredPrograms.map((program, pi) => (
          <div key={pi} className="structure-program">
            <button
              className="program-header"
              onClick={() => toggleExpand(`prog-${pi}`)}
            >
              {expandedItems.has(`prog-${pi}`) ? (
                <ChevronDown size={14} />
              ) : (
                <ChevronRight size={14} />
              )}
              <span className="program-name">{program.source_file || program.name}</span>
              <span className="item-count">{program.data_items?.length || 0} items</span>
            </button>

            {expandedItems.has(`prog-${pi}`) && (
              <div className="program-items">
                {program.data_items?.map((item, ii) => (
                  <div
                    key={ii}
                    className="data-item"
                    style={{ paddingLeft: `${(item.level || 1) * 12 + 20}px` }}
                  >
                    <span className="item-level">{item.level || '01'}</span>
                    <span className="item-name">{item.name}</span>
                    <span className="item-type">{item.type || item.picture || 'GROUP'}</span>
                    {item.occurs && (
                      <span className="item-occurs">OCCURS {item.occurs}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * Copybook Analysis Component
 */
function CopybookAnalysis({ copybooks }) {
  const [selectedCopybook, setSelectedCopybook] = useState(null)

  if (!copybooks?.copybooks?.length) {
    return (
      <div className="empty-state">
        <p>No copybook data available for this run.</p>
      </div>
    )
  }

  const copybookList = copybooks.copybooks || []

  return (
    <div className="copybook-analysis">
      <div className="copybook-list">
        <h4>Copybooks ({copybookList.length})</h4>
        {copybookList.map((cb, i) => (
          <div
            key={i}
            className={`copybook-item ${selectedCopybook?.name === cb.name ? 'selected' : ''}`}
            onClick={() => setSelectedCopybook(cb)}
          >
            <FileStack size={14} />
            <span className="copybook-name">{cb.name}</span>
            <span className="usage-count">
              {cb.field_count != null ? `${cb.field_count} fields` : `${cb.used_by?.length || 0} uses`}
            </span>
          </div>
        ))}
      </div>

      <div className="copybook-details">
        {selectedCopybook ? (
          <>
            <h4>{selectedCopybook.name}</h4>
            {selectedCopybook.classification && (
              <div className="detail-section">
                <label>Classification</label>
                <span className="usage-badge">{selectedCopybook.classification}</span>
              </div>
            )}
            <div className="detail-section">
              <label>Shared By</label>
              <div className="usage-list">
                {selectedCopybook.used_by?.length > 0
                  ? selectedCopybook.used_by.map((prog, i) => (
                      <span key={i} className="usage-badge">{prog}</span>
                    ))
                  : <span className="no-data">
                      {selectedCopybook.shared_by > 0
                        ? `${selectedCopybook.shared_by} program${selectedCopybook.shared_by !== 1 ? 's' : ''}`
                        : 'No usage data'}
                    </span>
                }
              </div>
            </div>
            <div className="detail-section">
              <label>Fields</label>
              <span className="no-data">{selectedCopybook.field_count ?? selectedCopybook.fields?.length ?? 0} total fields</span>
            </div>
          </>
        ) : (
          <div className="no-selection">
            <Info size={24} />
            <p>Select a copybook to view details</p>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Summary Panel
 */
function SummaryPanel({ data }) {
  const stats = useMemo(() => {
    const programs = data?.erd?.entities?.filter(e => e.type === 'program') || []
    const copybooks = data?.erd?.entities?.filter(e => e.type === 'copybook') || []
    const totalFields = copybooks.reduce((sum, cb) => sum + (cb.fieldCount || 0), 0)
    return {
      entities: programs.length || data?.erd?.entities?.length || 0,
      relationships: data?.erd?.relationships?.length || 0,
      fields: totalFields || data?.dataStructures?.summary?.total_data_items || 0,
      copybooks: copybooks.length || data?.copybookAnalysis?.copybooks?.length || 0,
    }
  }, [data])

  return (
    <div className="summary-panel">
      <div className="summary-grid">
        <div className="summary-stat">
          <Database size={20} />
          <div className="stat-content">
            <span className="stat-value">{stats.entities}</span>
            <span className="stat-label">Programs</span>
          </div>
        </div>
        <div className="summary-stat">
          <GitBranch size={20} />
          <div className="stat-content">
            <span className="stat-value">{stats.relationships}</span>
            <span className="stat-label">Relationships</span>
          </div>
        </div>
        <div className="summary-stat">
          <Table2 size={20} />
          <div className="stat-content">
            <span className="stat-value">{stats.fields}</span>
            <span className="stat-label">Data Fields</span>
          </div>
        </div>
        <div className="summary-stat">
          <FileStack size={20} />
          <div className="stat-content">
            <span className="stat-value">{stats.copybooks}</span>
            <span className="stat-label">Copybooks</span>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Main DataView Component
 *
 * @param {function} onBack          - Back button handler (hidden when embedded=true)
 * @param {object}   analysisContext - { jobs: { dataanalysis_job_id, codeanalysis_job_id }, baseName, mainProgram }
 * @param {boolean}  embedded        - When true, hides the top header/back-button so the component
 *                                     can be dropped directly into a tab panel
 */
function DataView({ onBack, analysisContext, embedded = false }) {
  const [activeTab, setActiveTab] = useState('erd')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [data, setData] = useState({
    erd: null,
    dataLineage: null,
    dataStructures: null,
    copybookAnalysis: null,
  })

  const dataAnalysisJobId = analysisContext?.jobs?.dataanalysis_job_id
  const codeAnalysisJobId = analysisContext?.jobs?.codeanalysis_job_id
  const mainProgram = analysisContext?.mainProgram

  const loadData = useCallback(async () => {
    if (!dataAnalysisJobId && !codeAnalysisJobId) {
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Resolve authoritative baseName from job record (context value may be app name not program name)
      let baseName = analysisContext?.baseName
      if (codeAnalysisJobId) {
        try {
          const jobMeta = await getJobResults(codeAnalysisJobId)
          if (jobMeta?.base_name) baseName = jobMeta.base_name
        } catch (e) {
          console.debug('[DataView] Could not resolve baseName from /results, using context value')
        }
      }

      let result = { erd: null, dataLineage: null, dataStructures: null, copybookAnalysis: null }

      // Try dedicated Data Analysis artifacts first
      if (dataAnalysisJobId) {
        result = await getDataAnalysisData(dataAnalysisJobId)
      }

      // Fall back to Code Analysis data for any sections still missing
      if (codeAnalysisJobId) {
        let crossRef = null
        let comprehensive = null
        let fileModel = null

        try {
          comprehensive = await getJsonArtifact(codeAnalysisJobId, 'comprehensive_parse_results.json')
          crossRef = comprehensive?.cross_reference || null
        } catch (err) {
          console.warn('[DataView] Could not load comprehensive_parse_results:', err.message)
          // fallback to getCrossReference
          try { crossRef = await getCrossReference(codeAnalysisJobId) } catch (e) {}
        }

        if (baseName) {
          try {
            fileModel = await getJsonArtifact(codeAnalysisJobId, `${baseName}_file_model.json`)
          } catch (err) {
            console.debug('[DataView] file_model not available:', err.message)
          }
        }

        // unified_data_model.items — real field definitions per source_file (CA only)
        const unifiedItems = comprehensive?.unified_data_model?.items || []

        // CA fallbacks — only fill slots that DA left empty.
        // copybookAnalysis: DA produces copybook_catalog (names + counts only, no program list)
        // dataStructures:   DA has no equivalent artifact — always sourced from CA
        // erd:              DA produces erd.json; fallback builds bipartite program↔copybook graph
        // dataLineage:      DA produces file I/O flows; fallback builds program→program call flows
        if (!result.copybookAnalysis?.copybooks?.length && crossRef) {
          result = { ...result, copybookAnalysis: buildCopybooksFromCrossRef(crossRef, unifiedItems) }
        }
        if (unifiedItems.length > 0) {
          result = { ...result, dataStructures: buildStructuresFromUnifiedModel(unifiedItems) }
        }
        // ERDGraph renders a COBOL program↔copybook bipartite layout.
        // The DA erd.json contains data-entity / FK relationships (different shape — different future view).
        // Always build the bipartite ERD from CA crossRef so the graph has the correct entity types.
        if (crossRef) {
          result = { ...result, erd: buildERDFromCrossRef(crossRef, unifiedItems) }
        }
        if (!result.dataLineage?.flows?.length && (crossRef || fileModel)) {
          result = { ...result, dataLineage: buildLineageFromCallGraph(crossRef, fileModel) }
        }
      }

      setData(result)
    } catch (err) {
      console.error('[DataView] Failed to fetch data:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [dataAnalysisJobId, codeAnalysisJobId, analysisContext, mainProgram])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleRefresh = useCallback(() => {
    loadData()
  }, [loadData])

  const renderTabContent = () => {
    if (loading) {
      return (
        <div className="loading-state">
          <Loader2 size={32} className="spin" />
          <p>Loading data analysis...</p>
        </div>
      )
    }

    if (error) {
      return (
        <div className="error-state">
          <AlertCircle size={32} />
          <p>{error}</p>
          <button onClick={handleRefresh}>Retry</button>
        </div>
      )
    }

    switch (activeTab) {
      case 'erd':
        return <ERDGraph erd={data.erd} />
      case 'lineage':
        return <DataLineageChart lineage={data.dataLineage} />
      case 'structures':
        return <DataStructuresBrowser structures={data.dataStructures} />
      case 'copybooks':
        return <CopybookAnalysis copybooks={data.copybookAnalysis} />
      default:
        return null
    }
  }

  return (
    <div className={`data-view${embedded ? ' data-view--embedded' : ''}`}>
      {/* Header — hidden when embedded inside a parent tab panel */}
      {!embedded && (
        <div className="view-header">
          <button className="back-btn" onClick={onBack}>
            <ArrowLeft size={18} />
            Back
          </button>
          <div className="header-title">
            <Database size={20} />
            <h2>Data Analysis</h2>
          </div>
          <div></div>
        </div>
      )}

      {/* Summary Panel */}
      <SummaryPanel data={data} />

      {/* Tab Navigation */}
      <div className="tab-nav">
        {Object.entries(TABS).map(([key, tab]) => {
          const Icon = tab.icon
          return (
            <button
              key={key}
              className={`tab-btn ${activeTab === key ? 'active' : ''}`}
              onClick={() => setActiveTab(key)}
            >
              <Icon size={16} />
              <span>{tab.label}</span>
            </button>
          )
        })}
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {renderTabContent()}
      </div>
    </div>
  )
}

export default DataView
